/**
 * Checks if a string is a valid email address.
 *
 * Uses a pragmatic regex that covers common valid emails while avoiding false positives.
 * Does not aim to be fully RFC 5322 compliant.
 *
 * @param value - The value to check.
 * @returns True if the value is a string matching a valid email format, false otherwise.
 */
export function isEmail(value: unknown): boolean {
  try {
    if (typeof value !== 'string') return false

    // Trim surrounding whitespace
    const input = value.trim()
    if (input.length === 0) return false

    // Basic structure: local@domain.tld (with optional subdomains)
    // - Local part: letters, numbers, and common specials . _ % + -
    // - Domain: letters, numbers, hyphens and dots
    // - TLD: at least 2 letters
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

    // Disallow consecutive dots in local or domain, and leading/trailing dots or hyphens
    if (!emailRegex.test(input)) return false

    const [local, domain] = input.split('@')
    if (!local || !domain) return false

    if (local.startsWith('.') || local.endsWith('.')) return false
    if (domain.startsWith('-') || domain.endsWith('-')) return false
    if (domain.startsWith('.') || domain.endsWith('.')) return false

    if (local.includes('..')) return false
    if (domain.includes('..')) return false

    // Each domain label must not start/end with hyphen and must be non-empty
    const labels = domain.split('.')
    if (labels.some(l => l.length === 0)) return false
    if (labels.some(l => l.startsWith('-') || l.endsWith('-'))) return false

    return true
  } catch {
    return false
  }
}
